import { Injectable } from '@angular/core';
import {
  ParsedField,
  SplitCondition,
  FromClauseInfo,
  OPERATOR_MAP
} from '../../interfaces/dev-tool/db-viewer.interface';
import { TabSdk } from '@techextensor/tab-sdk';
import { SortSequence } from '@techextensor/tab-core-utility';

@Injectable({
  providedIn: 'root'
})
export class SqlParserService {

  // Pre-compiled regex patterns (compiled once at class initialization)
  private readonly REGEX = {
    SELECT: /SELECT\s+(.*?)\s+FROM/is,
    FROM: /FROM\s+(\w+)(?:\s+(?:AS\s+)?(\w+))?/gi,
    JOIN: /(INNER|LEFT|RIGHT|FULL)?\s+JOIN\s+(\w+)(?:\s+(?:AS\s+)?(\w+))?\s+ON\s+([\w.]+)\s*=\s*([\w.]+)/gi,
    SINGLE_LINE_COMMENT: /--.*$/gm,
    MULTI_LINE_COMMENT: /\/\*[\s\S]*?\*\//g,
    LIMIT_OFFSET: /LIMIT\s+(\d+)\s+OFFSET\s+(\d+)/i,
    LIMIT_COMMA: /LIMIT\s+(\d+)\s*,\s*(\d+)/i,
    LIMIT_SIMPLE: /LIMIT\s+(\d+)/i,
    ORDER_BY_FIELD: /(.+?)\s+(ASC|DESC)/i,
    PARAMETER: /@(\w+)/g,
    AGGREGATE_FUNCTION: /^\s*(COUNT|SUM|AVG|MAX|MIN|GROUP_CONCAT|STRING_AGG|ARRAY_AGG)\s*\((.+?)\)(.*)$/i
  };

  // LRU Cache for parsed queries
  private parseCache = new Map<string, any>();
  private readonly MAX_CACHE_SIZE = 50;
  
  /**
   * Parse SQL query string into structured format (with caching)
   */
  parseQuery(sqlQuery: string, queryName: string = '') {
    const cacheKey = this.hashQuery(sqlQuery);
    
    if (this.parseCache.has(cacheKey)) {
      return this.parseCache.get(cacheKey);
    }
    
    const result = this.parseQueryInternal(sqlQuery, queryName);
    this.addToCache(cacheKey, result);
    
    return result;
  }
  
  /**
   * Internal parse logic (without cache)
   */
  private parseQueryInternal(sqlQuery: string, queryName: string = '') {
    // Extract query name from comment if not provided
    if (!queryName) {
      queryName = this.extractQueryName(sqlQuery);
    }
    
    // Remove comments once
    const cleanedQuery = this.removeComments(sqlQuery);
    
    // Extract SELECT clause and parse fields ONCE
    const selectClause = this.extractSelectClause(cleanedQuery);
    const parsedFields = this.parseFieldsUnified(selectClause);
    
    // Separate aggregate and non-aggregate fields
    const aggregateFields = parsedFields.filter(f => f.isAggregate);
    const nonAggregateFields = parsedFields.filter(f => !f.isAggregate);
    
    const selectedFields = nonAggregateFields.map(f => f.expression);
    const joins = this.parseJoins(cleanedQuery);
    const whereClause = this.parseWhereClause(cleanedQuery);
    const groupBy = this.parseGroupBy(cleanedQuery);
    const sort = this.parseOrderBy(cleanedQuery);
    const pager = this.parseLimit(cleanedQuery);
    const parameters = this.parseParameters(cleanedQuery);

    return {
      QueryName: queryName || '',
      SelectedFields: selectedFields,
      WhereClause: whereClause,
      GroupBy: groupBy.length > 0 ? groupBy : undefined,
      Sort: sort.length > 0 ? sort : undefined,
      Joins: joins.length > 0 ? joins : undefined,
      Pager: pager,
      Parameters: parameters
    };
  }
  
  /**
   * Convert SQL to JSON (with caching)
   */
  sqlToJson(sqlQuery: string, schemaData?: any): any {
    const cacheKey = this.hashQuery(sqlQuery + (schemaData ? '_schema' : ''));
    
    if (this.parseCache.has(cacheKey)) {
      return this.parseCache.get(cacheKey);
    }
    
    const result = this.sqlToJsonInternal(sqlQuery, schemaData);
    this.addToCache(cacheKey, result);
    
    return result;
  }
  
  /**
   * Internal SQL to JSON conversion (without cache)
   */
  private sqlToJsonInternal(sqlQuery: string, schemaData?: any): any {
    // Remove comments
    const cleanedQuery = this.removeComments(sqlQuery);
    
    // Detect DISTINCT and TOP
    const hasDistinct = /^\s*SELECT\s+DISTINCT\b/i.test(cleanedQuery);
    const topMatch = cleanedQuery.match(/SELECT\s+TOP\s+(\d+)/i);
    const topCount = topMatch ? parseInt(topMatch[1], 10) : undefined;
    
    // Extract FROM clause
    const fromInfo = this.extractFromClause(cleanedQuery);
    const queryObjectID = fromInfo?.tableName || '';
    const tableAlias = fromInfo?.alias;
    
    // Build alias to table name mapping
    const aliasToTable = new Map<string, string>();
    if (tableAlias) {
      aliasToTable.set(tableAlias.toLowerCase(), queryObjectID.toLowerCase());
    }
    aliasToTable.set(queryObjectID.toLowerCase(), queryObjectID.toLowerCase());
    
    // Parse SELECT fields ONCE using unified parser
    const selectClause = this.extractSelectClause(cleanedQuery);
    const parsedFields = this.parseFieldsUnified(selectClause);
    
    // Separate aggregate and non-aggregate fields
    const aggregateFields = parsedFields.filter(f => f.isAggregate);
    const nonAggregateFields = parsedFields.filter(f => !f.isAggregate);
    
    const resultField_AppfieldIds = nonAggregateFields.map(f => f.expression);
    const rawSQL_AppfieldIds = aggregateFields.map(f => ({
      Data: f.expression,
      DisplayName: f.alias || ''
    }));
    
    // Parse WHERE clause
    const whereClauseContent = this.extractClause(cleanedQuery, 'WHERE', ['GROUP BY', 'ORDER BY', 'HAVING', 'LIMIT']);
    const whereClause = whereClauseContent
      ? this.parseClauseConditions(whereClauseContent, false, queryObjectID)
      : { Filters: [] };

    // Parse GROUP BY
    const groupByContent = this.extractClause(cleanedQuery, 'GROUP BY', ['HAVING', 'ORDER BY', 'LIMIT']);
    const groupByFields = groupByContent ? this.splitByComma(groupByContent) : [];

    // Parse HAVING clause
    const havingContent = this.extractClause(cleanedQuery, 'HAVING', ['ORDER BY', 'LIMIT']);
    const havingClause = havingContent
      ? this.parseClauseConditions(havingContent, true, queryObjectID)
      : { Filters: [] };
    
    // Parse ORDER BY
    const orderByContent = this.extractClause(cleanedQuery, 'ORDER BY', ['LIMIT']);
    const sort = orderByContent ? this.parseOrderByContent(orderByContent) : [];
    
    // Parse JOINs
    const joins = this.parseJoinsForJson(cleanedQuery, aliasToTable, schemaData);
    
    const result: any = {
      QueryObjectID: queryObjectID,
      ResultField_AppfieldIds: resultField_AppfieldIds,
      RawSQL_AppfieldIds: rawSQL_AppfieldIds,
      GroupByFields: groupByFields,
      WhereClause: whereClause.Filters.length > 0 ? whereClause : {},
      HavingClause: havingClause.Filters.length > 0 ? havingClause : {},
      Joins: joins,
      Sort: sort
    };
    
    if (topCount !== undefined) {
      result.TopCount = topCount;
    }
    
    if (hasDistinct) {
      result.Distinct = true;
    }
    
    return result;
  }
  
  /**
   * Convert JSON to SQL
   */
  jsonToSql(jsonQuery: any): string {
    const parts: string[] = [];
    
    // SELECT clause
    parts.push('SELECT');
    if (jsonQuery.ResultField_AppfieldIds && jsonQuery.ResultField_AppfieldIds.length > 0) {
      parts.push('    ' + jsonQuery.ResultField_AppfieldIds.join(',\n    '));
    } else {
      parts.push('    *');
    }
    
    // FROM clause
    if (jsonQuery.QueryObjectID) {
      parts.push('FROM ' + jsonQuery.QueryObjectID);
    } else {
      throw new Error('QueryObjectID is required');
    }
    
    // JOIN clauses
    if (jsonQuery.Joins && jsonQuery.Joins.length > 0) {
      jsonQuery.Joins.forEach((join: any) => {
        const joinTypeMap: { [key: number]: string } = {
          1: 'INNER', 2: 'LEFT', 3: 'RIGHT', 4: 'FULL'
        };
        const joinType = joinTypeMap[join.JoinType] || 'INNER';
        
        if (join.Relationship) {
          const rightObject = join.RightObject || 'JoinedTable';
          parts.push(`${joinType} JOIN ${rightObject}`);
          if (join.RightObjectAlias) {
            parts.push(` ${join.RightObjectAlias}`);
          }
          parts.push(` ON ${join.LeftField || 'SourceField'} = ${join.RightField || 'TargetField'}`);
        } else {
          const rightObject = join.RightObject || 'JoinedTable';
          parts.push(`${joinType} JOIN ${rightObject}`);
          if (join.RightObjectAlias) {
            parts.push(` ${join.RightObjectAlias}`);
          }
          parts.push(` ON ${join.LeftField} = ${join.RightField}`);
        }
      });
    }
    
    // WHERE clause
    if (jsonQuery.WhereClause && jsonQuery.WhereClause.Filters && jsonQuery.WhereClause.Filters.length > 0) {
      const conditions: string[] = [];
      
      jsonQuery.WhereClause.Filters.forEach((filter: any, index: number) => {
        const condition = this.buildFilterConditionFromJson(filter);
        if (condition) {
          if (index > 0) {
            const conjunction = filter.ConjuctionClause === 2 ? 'OR' :
                              filter.ConjuctionClause === 3 ? 'AND NOT' :
                              filter.ConjuctionClause === 4 ? 'OR NOT' :
                              filter.ConjuctionClause === 5 ? 'IN' : 'AND';
            conditions.push(conjunction + ' ' + condition);
          } else {
            conditions.push(condition);
          }
        }
      });
      
      parts.push('WHERE ' + conditions.join(' '));
    }
    
    // GROUP BY clause
    if (jsonQuery.GroupByFields && jsonQuery.GroupByFields.length > 0) {
      parts.push('GROUP BY ' + jsonQuery.GroupByFields.join(', '));
    }
    
    // HAVING clause
    if (jsonQuery.HavingClause && jsonQuery.HavingClause.Filters && jsonQuery.HavingClause.Filters.length > 0) {
      const conditions: string[] = [];
      
      jsonQuery.HavingClause.Filters.forEach((filter: any, index: number) => {
        const condition = this.buildFilterConditionFromJson(filter);
        if (condition) {
          if (index > 0) {
            const conjunction = filter.ConjuctionClause === 2 ? 'OR' :
                              filter.ConjuctionClause === 3 ? 'AND NOT' :
                              filter.ConjuctionClause === 4 ? 'OR NOT' :
                              filter.ConjuctionClause === 5 ? 'IN' : 'AND';
            conditions.push(conjunction + ' ' + condition);
          } else {
            conditions.push(condition);
          }
        }
      });
      
      parts.push('HAVING ' + conditions.join(' '));
    }
    
    // ORDER BY clause
    if (jsonQuery.Sort && jsonQuery.Sort.length > 0) {
      const sortFields: string[] = [];
      const sortedSorts = [...jsonQuery.Sort].sort((a: any, b: any) => {
        if (a.SortSequence !== b.SortSequence) {
          return (a.SortSequence || 0) - (b.SortSequence || 0);
        }
        return (a.Sequence || 0) - (b.Sequence || 0);
      });
      
      sortedSorts.forEach((sort: any) => {
        const fieldName = sort.FieldID || sort.FieldName;
        const direction = sort.Direction?.toUpperCase() || (sort.SortSequence === 2 ? 'DESC' : 'ASC');
        sortFields.push(fieldName + ' ' + direction);
      });
      
      parts.push('ORDER BY ' + sortFields.join(', '));
    }
    
    return parts.join('\n') + ';';
  }
  
  /**
   * Clear parse cache
   */
  clearCache(): void {
    this.parseCache.clear();
  }
  
  // ==================== UNIFIED UTILITIES ====================
  
  /**
   * UNIFIED FIELD PARSER - Replaces 3 separate methods
   * Parses SELECT fields once, returns structured data
   */
  private parseFieldsUnified(selectClause: string): ParsedField[] {
    if (!selectClause) return [];
    
    // Remove DISTINCT and TOP
    let fieldsSection = selectClause
      .replace(/^\s*DISTINCT\s+/i, '')
      .replace(/^\s*TOP\s+\d+\s+/i, '')
      .trim();
    
    if (fieldsSection === '*') {
      return [{
        raw: '*',
        expression: '*',
        alias: null,
        isAggregate: false,
        aggregateFunction: null,
        innerField: null
      }];
    }
    
    // Split by comma
    const fieldStrings = this.splitByComma(fieldsSection);
    
    return fieldStrings.map(fieldStr => this.parseField(fieldStr));
  }
  
  /**
   * Parse a single field
   */
  private parseField(fieldStr: string): ParsedField {
    const trimmed = fieldStr.trim();
    
    // Check for aggregate function
    const aggregateMatch = trimmed.match(this.REGEX.AGGREGATE_FUNCTION);
    
    if (aggregateMatch) {
      const func = aggregateMatch[1].toUpperCase();
      const innerField = aggregateMatch[2].trim();
      const afterFunc = aggregateMatch[3].trim();
      
      // Extract alias
      let alias: string | null = null;
      const asMatch = afterFunc.match(/^\s*AS\s+(\w+)/i);
      if (asMatch) {
        alias = asMatch[1];
      } else {
        const implicitMatch = afterFunc.match(/^\s+(\w+)$/);
        if (implicitMatch) {
          alias = implicitMatch[1];
        }
      }
      
      if (!alias) {
        alias = `${func}_${innerField.replace(/[^a-zA-Z0-9]/g, '')}`.toLowerCase();
      }
      
      return {
        raw: trimmed,
        expression: `${func}(${innerField})`,
        alias,
        isAggregate: true,
        aggregateFunction: func,
        innerField
      };
    } else {
      // Regular field
      let expression = trimmed;
      let alias: string | null = null;
      
      const asMatch = trimmed.match(/^(.+?)\s+AS\s+(\w+)$/i);
      if (asMatch) {
        expression = asMatch[1].trim();
        alias = asMatch[2];
      } else {
        const implicitMatch = trimmed.match(/^(.+?)\s+(\w+)$/);
        if (implicitMatch && !implicitMatch[1].includes('(')) {
          expression = implicitMatch[1].trim();
          alias = implicitMatch[2];
        }
      }
      
      return {
        raw: trimmed,
        expression,
        alias,
        isAggregate: false,
        aggregateFunction: null,
        innerField: null
      };
    }
  }
  
  /**
   * UNIFIED CONDITION PARSER - Replaces parseCondition + parseHavingCondition
   * Works for both WHERE and HAVING clauses
   */
  private parseConditionUnified(
    condition: string,
    sequence: number,
    groupId: number,
    allowAggregates: boolean = false,
    tableName?: string
  ): any | null {
    condition = condition.replace(/\s+(AND|OR)$/i, '').trim();
    condition = condition.replace(/;+\s*$/, '').trim();

    const fieldPattern = allowAggregates
      ? /^(\w+\s*\([^)]+\)|[\w.]+)/
      : /^([\w.]+)/;

    // Helper function to get field ID from metadata
    const getFieldId = (fieldName: string): string => {
      if (!tableName) return this.generateGuid();

      try {
        const lowerTableName = tableName.toLowerCase();
        const appObject = TabSdk.metadataUtils.getAllAppObjects(
          a => a.ObjectName?.toLowerCase() === lowerTableName || a.DisplayName?.toLowerCase() === lowerTableName
        )[0];

        if (appObject) {
          const lowerFieldName = fieldName.toLowerCase();
          const field = appObject?.findFields(f => f.FieldName?.toLowerCase() === lowerFieldName || f.DisplayName?.toLowerCase() === lowerFieldName)[0];
          
          if (field) {
            return field.ID;
          }
        }
      } catch (error) {
        console.warn(`Failed to get field ID for ${fieldName} in ${tableName}:`, error);
      }

      return this.generateGuid();
    };

    // IS NULL / IS NOT NULL
    const isNullMatch = condition.match(new RegExp(`${fieldPattern.source}\\s+(IS\\s+(NOT\\s+)?NULL)`, 'i'));
    if (isNullMatch) {
      const fieldName = isNullMatch[1];
      return {
        ConjuctionClause: 1,
        FieldID: getFieldId(fieldName),
        RelationalOperator: isNullMatch[2].toUpperCase().includes('NOT') ? 7 : 6,
        Value: '',
        ValueType: 1,
        GroupID: groupId,
        Sequence: sequence,
        FieldType: 1,
        FieldName: fieldName,
        LookUpDetail: null,
        ID: this.generateGuid()
      };
    }

    // IN / NOT IN
    const inMatch = condition.match(new RegExp(`${fieldPattern.source}\\s+(NOT\\s+)?IN\\s*\\(([^)]+)\\)`, 'i'));
    if (inMatch) {
      const fieldName = inMatch[1];
      const isNot = !!inMatch[2];
      let values = inMatch[3].trim().replace(/;+\s*$/, '').trim();
      const isParameter = values.startsWith('@');

      return {
        ConjuctionClause: 1,
        FieldID: getFieldId(fieldName),
        RelationalOperator: isNot ? 5 : 4,
        Value: values,
        ValueType: isParameter ? 2 : 1,
        GroupID: groupId,
        Sequence: sequence,
        FieldType: 1,
        FieldName: fieldName,
        LookUpDetail: null,
        ID: this.generateGuid()
      };
    }

    // BETWEEN / NOT BETWEEN
    const betweenMatch = condition.match(new RegExp(`${fieldPattern.source}\\s+(NOT\\s+)?BETWEEN\\s+(.+?)\\s+AND\\s+(.+)`, 'i'));
    if (betweenMatch) {
      const fieldName = betweenMatch[1];
      const isNot = !!betweenMatch[2];
      let value1 = betweenMatch[3].trim().replace(/;+\s*$/, '').replace(/^['"]|['"]$/g, '');
      let value2 = betweenMatch[4].trim().replace(/;+\s*$/, '').replace(/^['"]|['"]$/g, '');

      return {
        ConjuctionClause: 1,
        FieldID: getFieldId(fieldName),
        RelationalOperator: isNot ? 18 : 17,
        Value: `${value1}|${value2}`,
        ValueType: 1,
        GroupID: groupId,
        Sequence: sequence,
        FieldType: 1,
        FieldName: fieldName,
        LookUpDetail: null,
        ID: this.generateGuid()
      };
    }

    // LIKE / NOT LIKE
    const likeMatch = condition.match(new RegExp(`${fieldPattern.source}\\s+(NOT\\s+)?LIKE\\s+(.+)`, 'i'));
    if (likeMatch) {
      const fieldName = likeMatch[1];
      const isNot = !!likeMatch[2];
      let value = likeMatch[3].trim().replace(/;+\s*$/, '').replace(/^['"]|['"]$/g, '');
      const isParameter = value.startsWith('@');

      let operator = 11;
      if (value.startsWith('%') && value.endsWith('%')) {
        operator = isNot ? 12 : 11;
      } else if (value.endsWith('%')) {
        operator = isNot ? 14 : 13;
      } else if (value.startsWith('%')) {
        operator = isNot ? 16 : 15;
      } else {
        operator = isNot ? 12 : 11;
      }

      return {
        ConjuctionClause: 1,
        FieldID: getFieldId(fieldName),
        RelationalOperator: operator,
        Value: value,
        ValueType: isParameter ? 2 : 1,
        GroupID: groupId,
        Sequence: sequence,
        FieldType: 1,
        FieldName: fieldName,
        LookUpDetail: null,
        ID: this.generateGuid()
      };
    }

    // Comparison operators
    const comparisonMatch = condition.match(new RegExp(`${fieldPattern.source}\\s*(=|!=|<>|>=|<=|>|<)\\s*(.+)`, 'i'));
    if (comparisonMatch) {
      const fieldName = comparisonMatch[1];
      const operator = comparisonMatch[2];
      let value = comparisonMatch[3].trim().replace(/;+\s*$/, '');

      if (/^['"](.*)['"]$/.test(value)) {
        value = value.replace(/^['"]|['"]$/g, '');
      }

      const isParameter = value.startsWith('@');

      return {
        ConjuctionClause: 1,
        FieldID: getFieldId(fieldName),
        RelationalOperator: OPERATOR_MAP[operator] || 3,
        Value: value,
        ValueType: isParameter ? 2 : 1,
        GroupID: groupId,
        Sequence: sequence,
        FieldType: 1,
        FieldName: fieldName,
        LookUpDetail: null,
        ID: this.generateGuid()
      };
    }

    return null;
  }
  
  /**
   * Split text by comma, respecting quotes and parentheses
   */
  private splitByComma(text: string): string[] {
    const results: string[] = [];
    let current = '';
    let depth = 0;
    let inQuotes = false;
    let quoteChar = '';
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const prevChar = i > 0 ? text[i - 1] : '';
      
      if ((char === '"' || char === "'") && prevChar !== '\\') {
        if (!inQuotes) {
          inQuotes = true;
          quoteChar = char;
        } else if (char === quoteChar) {
          inQuotes = false;
        }
      }
      
      if (!inQuotes) {
        if (char === '(') {
          depth++;
        } else if (char === ')') {
          depth--;
        } else if (char === ',' && depth === 0) {
          if (current.trim()) {
            results.push(current.trim());
          }
          current = '';
          continue;
        }
      }
      
      current += char;
    }
    
    if (current.trim()) {
      results.push(current.trim());
    }
    
    return results;
  }
  
  /**
   * Split conditions by AND/OR, respecting BETWEEN clauses
   */
  private splitConditions(text: string): SplitCondition[] {
    const results: SplitCondition[] = [];
    let current = '';
    let depth = 0;
    let inQuotes = false;
    let quoteChar = '';
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const prevChar = i > 0 ? text[i - 1] : '';
      
      if ((char === '"' || char === "'") && prevChar !== '\\') {
        if (!inQuotes) {
          inQuotes = true;
          quoteChar = char;
        } else if (char === quoteChar) {
          inQuotes = false;
        }
      }
      
      if (!inQuotes) {
        if (char === '(') {
          depth++;
        } else if (char === ')') {
          depth--;
        } else if (depth === 0) {
          const remaining = text.substring(i);
          
          // Check for AND (not part of BETWEEN)
          if (remaining.match(/^\s+AND\s+/i)) {
            const textBeforeAnd = current.trim().toUpperCase();
            const hasBetween = textBeforeAnd.includes('BETWEEN');
            const hasAndAfterBetween = hasBetween && textBeforeAnd.match(/BETWEEN.*\sAND\s/i);
            
            if (!hasBetween || hasAndAfterBetween) {
              if (current.trim()) {
                results.push({ condition: current.trim() });
              }
              current = '';
              const andMatch = remaining.match(/^\s+AND\s+/i);
              i += andMatch![0].length - 1;
              if (results.length > 0) {
                results[results.length - 1].conjunction = 'AND';
              }
              continue;
            }
          }
          
          // Check for OR
          if (remaining.match(/^\s+OR\s+/i)) {
            if (current.trim()) {
              results.push({ condition: current.trim() });
            }
            current = '';
            const orMatch = remaining.match(/^\s+OR\s+/i);
            i += orMatch![0].length - 1;
            if (results.length > 0) {
              results[results.length - 1].conjunction = 'OR';
            }
            continue;
          }
        }
      }
      
      current += char;
    }
    
    if (current.trim()) {
      results.push({ condition: current.trim() });
    }
    
    return results.length > 0 ? results : [{ condition: text }];
  }
  
  /**
   * Extract clause content between keyword and stop keywords
   */
  private extractClause(sql: string, keyword: string, stopKeywords: string[]): string | null {
    const keywordRegex = new RegExp(`\\b${keyword}\\b`, 'i');
    const keywordMatch = sql.match(keywordRegex);
    if (!keywordMatch || keywordMatch.index === undefined) {
      return null;
    }
    
    const startPos = keywordMatch.index + keywordMatch[0].length;
    const afterKeyword = sql.substring(startPos);
    
    let endPos = afterKeyword.length;
    
    for (const stopKeyword of stopKeywords) {
      const stopRegex = new RegExp(`\\b${stopKeyword.replace(/\s+/g, '\\s+')}\\b`, 'i');
      const stopMatch = afterKeyword.match(stopRegex);
      if (stopMatch && stopMatch.index !== undefined && stopMatch.index < endPos) {
        endPos = stopMatch.index;
      }
    }
    
    const semicolonPos = afterKeyword.indexOf(';');
    if (semicolonPos !== -1 && semicolonPos < endPos) {
      endPos = semicolonPos;
    }
    
    const clauseContent = afterKeyword.substring(0, endPos).trim();
    return clauseContent || null;
  }
  
  // ==================== CLAUSE PARSERS ====================
  
  /**
   * Parse clause conditions (WHERE or HAVING)
   */
  private parseClauseConditions(
    clauseContent: string,
    allowAggregates: boolean,
    tableName?: string
  ): any {
    const conditions = this.splitConditions(clauseContent);
    const filters: any[] = [];

    conditions.forEach((condData, index) => {
      const filter = this.parseConditionUnified(
        condData.condition,
        index + 1,
        1,
        allowAggregates,
        tableName
      );

      if (filter) {
        filter.ConjuctionClause = index === 0 ? 1 : (condData.conjunction === 'OR' ? 2 : 1);
        filters.push(filter);
      }
    });

    return { Filters: filters };
  }
  
  /**
   * Extract SELECT clause
   */
  private extractSelectClause(sql: string): string {
    const selectMatch = sql.match(this.REGEX.SELECT);
    return selectMatch ? selectMatch[1].trim() : '';
  }
  
  /**
   * Extract FROM clause info
   */
  private extractFromClause(sql: string): FromClauseInfo | null {
    const fromMatch = sql.match(/FROM\s+(\w+)(?:\s+(?:AS\s+)?(\w+))?/i);
    if (!fromMatch) return null;
    
    return {
      tableName: fromMatch[1],
      alias: fromMatch[2]
    };
  }
  
  /**
   * Extract query name from comment
   */
  private extractQueryName(sql: string): string {
    const singleLineComment = sql.match(/^\s*--\s*(.+?)(?:\r?\n|$)/m);
    if (singleLineComment) {
      return singleLineComment[1].trim();
    }
    
    const multiLineComment = sql.match(/^\s*\/\*\s*(.+?)\s*\*\//s);
    if (multiLineComment) {
      return multiLineComment[1].trim().split('\n')[0].trim();
    }
    
    return '';
  }
  
  /**
   * Remove SQL comments
   */
  private removeComments(sql: string): string {
    sql = sql.replace(this.REGEX.SINGLE_LINE_COMMENT, '');
    sql = sql.replace(this.REGEX.MULTI_LINE_COMMENT, '');
    return sql.trim();
  }
  
  /**
   * Parse WHERE clause
   */
  private parseWhereClause(sql: string) {
    const whereContent = this.extractClause(sql, 'WHERE', ['GROUP BY', 'ORDER BY', 'HAVING', 'LIMIT']);
    if (!whereContent) return undefined;
    
    return this.parseClauseConditions(whereContent, false);
  }
  
  /**
   * Parse JOIN clauses
   */
  private parseJoins(sql: string) {
    const joins: any[] = [];
    const joinRegex = this.REGEX.JOIN;
    let match: RegExpExecArray | null;
    
    while ((match = joinRegex.exec(sql)) !== null) {
      const joinType = (match[1] || 'INNER').toUpperCase().trim() as 'INNER' | 'LEFT' | 'RIGHT' | 'FULL';
      const rightObject = match[2];
      const alias = match[3] || '';
      
      let leftField = match[4].trim();
      let rightField = match[5].trim();
      
      const rightFieldUsesTable = rightField.startsWith(rightObject + '.') || 
                                  (alias && rightField.startsWith(alias + '.'));
      
      if (rightFieldUsesTable && !leftField.includes('.')) {
        [leftField, rightField] = [rightField, leftField];
      } else if (leftField.startsWith(rightObject + '.') || (alias && leftField.startsWith(alias + '.'))) {
        [leftField, rightField] = [rightField, leftField];
      }
      
      joins.push({
        JoinType: joinType,
        RightObject: rightObject,
        LeftField: leftField,
        RightField: rightField
      });
    }
    
    return joins;
  }
  
  /**
   * Parse JOINs for JSON output
   */
  private parseJoinsForJson(sql: string, aliasToTable: Map<string, string>, schemaData?: any): any[] {
    const joins: any[] = [];
    const joinRegex = this.REGEX.JOIN;
    let match: RegExpExecArray | null;
    
    while ((match = joinRegex.exec(sql)) !== null) {
      const joinType = match[1] || 'INNER';
      const joinTypeMap: { [key: string]: number } = {
        'LEFT': 0, 'INNER': 1, 'RIGHT': 2, 'FULL': 3
      };
      const joinTypeNumber = joinTypeMap[joinType.toUpperCase()] ?? 1;
      
      const rightObject = match[2];
      const alias = match[3] || '';
      let leftField = match[4].trim();
      let rightField = match[5].trim();
      
      const leftTableMatch = leftField.match(/^(\w+)\./);
      const rightTableMatch = rightField.match(/^(\w+)\./);
      
      let sourceTableName = leftTableMatch 
        ? (aliasToTable.get(leftTableMatch[1].toLowerCase()) || leftTableMatch[1])
        : '';
      let sourceFieldName = leftTableMatch ? leftField.split('.')[1] : leftField;
      
      let targetTableName = rightTableMatch 
        ? (aliasToTable.get(rightTableMatch[1].toLowerCase()) || rightTableMatch[1])
        : rightObject;
      let targetFieldName = rightTableMatch ? rightField.split('.')[1] : rightField;
      
      const sourceTableId = schemaData 
        ? (this.getTableIdFromSchema(schemaData, sourceTableName) || this.generateGuid())
        : this.generateGuid();
      const targetTableId = schemaData 
        ? (this.getTableIdFromSchema(schemaData, targetTableName) || this.generateGuid())
        : this.generateGuid();
      
      const sourceFieldId = schemaData 
        ? (this.getFieldIdFromSchema(schemaData, sourceTableName, sourceFieldName) || this.generateGuid())
        : this.generateGuid();
      const targetFieldId = schemaData 
        ? (this.getFieldIdFromSchema(schemaData, targetTableName, targetFieldName) || this.generateGuid())
        : this.generateGuid();
      
      joins.push({
        JoinType: joinTypeNumber,
        Relationship: {
          RelSourceObjectID: sourceTableId,
          RelSourceFieldID: sourceFieldId,
          RelTargetObjectID: targetTableId,
          RelTargetFieldID: targetFieldId
        }
      });
    }
    
    return joins;
  }
  
  /**
   * Parse GROUP BY clause
   */
  private parseGroupBy(sql: string): string[] {
    const groupByContent = this.extractClause(sql, 'GROUP BY', ['HAVING', 'ORDER BY', 'LIMIT']);
    return groupByContent ? this.splitByComma(groupByContent) : [];
  }
  
  /**
   * Parse ORDER BY clause
   */
  private parseOrderBy(sql: string) {
    const orderByContent = this.extractClause(sql, 'ORDER BY', ['LIMIT']);
    return orderByContent ? this.parseOrderByContent(orderByContent) : [];
  }
  
  /**
   * Parse ORDER BY content
   */
  private parseOrderByContent(orderByContent: string): any[] {
    const sortFields: any[] = [];
    const fieldParts = this.splitByComma(orderByContent);

    for (let i = 0; i < fieldParts.length; i++) {
      const part = fieldParts[i];
      const trimmed = part.trim();
      const descMatch = trimmed.match(this.REGEX.ORDER_BY_FIELD);

      if (descMatch) {
        const direction = descMatch[2].toUpperCase();
        sortFields.push({
          FieldID: descMatch[1].trim(),
          SortSequence: direction === 'DESC' ? SortSequence.Desc : SortSequence.Asc,
          Sequence: i + 1
        });
      } else {
        sortFields.push({
          FieldID: trimmed,
          SortSequence: SortSequence.Asc,
          Sequence: i + 1
        });
      }
    }

    return sortFields;
  }
  
  /**
   * Parse LIMIT clause
   */
  private parseLimit(sql: string) {
    let limitMatch = sql.match(this.REGEX.LIMIT_OFFSET);
    if (limitMatch) {
      const pageSize = parseInt(limitMatch[1], 10);
      const offset = parseInt(limitMatch[2], 10);
      return {
        PageSize: pageSize,
        PageNumber: Math.floor(offset / pageSize) + 1
      };
    }
    
    limitMatch = sql.match(this.REGEX.LIMIT_COMMA);
    if (limitMatch) {
      const offset = parseInt(limitMatch[1], 10);
      const pageSize = parseInt(limitMatch[2], 10);
      return {
        PageSize: pageSize,
        PageNumber: Math.floor(offset / pageSize) + 1
      };
    }
    
    limitMatch = sql.match(this.REGEX.LIMIT_SIMPLE);
    if (limitMatch) {
      const pageSize = parseInt(limitMatch[1], 10);
      return {
        PageSize: pageSize,
        PageNumber: 1
      };
    }
    
    return undefined;
  }
  
  /**
   * Parse parameters from query
   */
  private parseParameters(sql: string) {
    const matches = Array.from(sql.matchAll(this.REGEX.PARAMETER));
    const uniqueParams = [...new Set(matches.map(m => m[1]))];
    
    return uniqueParams.map(paramName => ({
      FieldName: paramName,
      IsMandatory: true,
      MappingFieldName: paramName
    }));
  }
  
  /**
   * Build filter condition from JSON
   */
  private buildFilterConditionFromJson(filter: any): string {
    if (!filter.FieldName) return '';
    
    const fieldName = filter.FieldName;
    const operator = this.getSQLOperatorFromNumber(filter.RelationalOperator || filter.Operator);
    let value = filter.Value;
    
    if (filter.RelationalOperator === 6 || filter.Operator === 6) {
      return `${fieldName} IS NULL`;
    }
    if (filter.RelationalOperator === 7 || filter.Operator === 7) {
      return `${fieldName} IS NOT NULL`;
    }
    
    if (filter.RelationalOperator === 17 || filter.RelationalOperator === 18 || 
        filter.Operator === 17 || filter.Operator === 18) {
      if (typeof value === 'string' && value.includes('|')) {
        const [value1, value2] = value.split('|').map(v => v.trim());
        const formattedValue1 = value1.startsWith('@') ? value1 : `'${value1}'`;
        const formattedValue2 = value2.startsWith('@') ? value2 : `'${value2}'`;
        return `${fieldName} ${operator} ${formattedValue1} AND ${formattedValue2}`;
      }
    }
    
    if (filter.ValueType === 2 && value && value.startsWith('@')) {
      return `${fieldName} ${operator} ${value}`;
    }
    
    if (typeof value === 'string' && !value.startsWith("'") && !value.startsWith('"') && !value.startsWith('@')) {
      value = value.replace(/^['"]|['"]$/g, '');
      value = `'${value}'`;
    }
    
    return `${fieldName} ${operator} ${value}`;
  }
  
  /**
   * Get SQL operator from number (public for components)
   */
  public getSQLOperatorFromNumber(operatorNumber: number): string {
    const operatorMap: { [key: number]: string } = {
      1: '>', 2: '<', 3: '=', 4: 'IN', 5: 'NOT IN',
      6: 'IS NULL', 7: 'IS NOT NULL', 8: '!=',
      9: '>=', 10: '<=', 11: 'LIKE', 12: 'NOT LIKE',
      13: 'LIKE', 14: 'NOT LIKE', 15: 'LIKE', 16: 'NOT LIKE',
      17: 'BETWEEN', 18: 'NOT BETWEEN', 19: 'LIKE', 20: 'NOT LIKE'
    };
    return operatorMap[operatorNumber] || '=';
  }
  
  /**
   * Look up table ID from schema
   */
  private getTableIdFromSchema(schemaData: any, tableName: string): string | null {
    if (!schemaData || !schemaData.appObjects || !tableName) {
      return null;
    }
    
    const table = schemaData.appObjects.find((obj: any) => {
      const objName = (obj.name || obj.ObjectName || obj.SystemDBTableName || '').toLowerCase();
      const searchName = tableName.toLowerCase();
      return objName === searchName;
    });
    
    return table ? (table.ID || table.id) : null;
  }
  
  /**
   * Look up field ID from schema
   */
  private getFieldIdFromSchema(schemaData: any, tableName: string, fieldName: string): string | null {
    if (!schemaData || !schemaData.appObjects || !tableName || !fieldName) {
      return null;
    }
    
    const table = schemaData.appObjects.find((obj: any) => {
      const objName = (obj.name || obj.ObjectName || obj.SystemDBTableName || '').toLowerCase();
      const searchName = tableName.toLowerCase();
      return objName === searchName;
    });
    
    if (!table || !table.fields) {
      return null;
    }
    
    const field = table.fields.find((f: any) => {
      const fName = (f.FieldName || f.name || f.SystemDBFieldName || '').toLowerCase();
      const searchName = fieldName.toLowerCase();
      return fName === searchName;
    });
    
    return field ? (field.ID || field.id) : null;
  }
  
  /**
   * Generate GUID
   */
  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
  
  /**
   * Hash query for caching
   */
  private hashQuery(sql: string): string {
    let hash = 0;
    for (let i = 0; i < sql.length; i++) {
      const char = sql.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  }
  
  /**
   * Add to LRU cache
   */
  private addToCache(key: string, value: any): void {
    if (this.parseCache.size >= this.MAX_CACHE_SIZE) {
      const firstKey: any = this.parseCache.keys().next().value;
      this.parseCache.delete(firstKey);
    }
    this.parseCache.set(key, value);
  }
}